// 📁 src/components/account/TenantSoftwarePanel.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TenantSoftwarePanel() {
  const navigate = useNavigate();

  const [moduleAccess, setModuleAccess] = useState({});
  const [paymentStatus, setPaymentStatus] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [auditAccessMap, setAuditAccessMap] = useState({});
  const [refreshTick, setRefreshTick] = useState(0); // ✅ force re-render

  useEffect(() => {
    const updateRegistry = () => {
      const email = localStorage.getItem('email');
      if (!email) return;

      const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
      const account = registry[email] || {};

      setModuleAccess(account.moduleAccess || {});
      setCompanyName(account.companyName || '');
      setTenantId(account.tenantId || '');

      const auditLog = JSON.parse(localStorage.getItem(`paymentAuditLog_${email}`) || '[]');
      const accessMap = {};
      const paymentMap = {};
      auditLog.forEach((entry) => {
        const normalizedKey = entry.moduleName === 'HRIS' ? 'PeopleSync' : entry.moduleName;
        accessMap[normalizedKey] = entry.paymentStatus === 'paid'; // ✅ payment-only logic
        paymentMap[normalizedKey] = entry.paymentStatus || 'unpaid';
      });
      setAuditAccessMap(accessMap);
      setPaymentStatus(paymentMap);

      setRefreshTick((tick) => tick + 1); // ✅ trigger re-render
    };

    updateRegistry(); // Initial load

    const interval = setInterval(updateRegistry, 1000);
    window.addEventListener('accountRegistryUpdated', updateRegistry);

    const handleStorageChange = (e) => {
      if (e.key === 'lastAuditUpdate') {
        updateRegistry();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('accountRegistryUpdated', updateRegistry);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const syncMatrix = {
    PeopleSync: ['ATS'],
    AuditReady: ['PeopleSync'],
    PerformEdge: ['PeopleSync'],
    ATS: [],
    Scout: []
  };

  const modules = {
    ATS: {
      name: 'ATS',
      description: 'Applicant Tracking System for streamlined hiring',
      route: 'ats'
    },
    PeopleSync: {
      name: 'PeopleSync',
      description: 'Human Resource Information System for employee records',
      route: 'peoplesync'
    },
    Scout: {
      name: 'Scout',
      description: 'Candidate sourcing and outreach automation',
      route: 'scout'
    },
    AuditReady: {
      name: 'AuditReady',
      description: 'Compliance and audit trail management suite',
      route: 'auditready'
    },
    PerformEdge: {
      name: 'PerformEdge',
      description: 'Performance reviews and goal tracking system',
      route: 'performedge'
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-indigo-700 mb-2">My Software</h2>
      {(companyName || tenantId) && (
        <div className="mb-4 text-sm text-gray-600 italic">
          {companyName && <div>Company Name: {companyName}</div>}
          {tenantId && <div>Tenant ID: {tenantId}</div>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.keys(modules).map((key) => {
          const mod = modules[key];
          const hasAccess = auditAccessMap[key] === true;
          const payment = paymentStatus[key] === 'paid' ? 'paid' : 'unpaid';

          return (
            <div
              key={key}
              className="border rounded-lg shadow-sm p-4 bg-white hover:shadow-md transition"
            >
              <h3 className="text-lg font-bold text-gray-800">{mod.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{mod.description}</p>

              {syncMatrix[key]?.length > 0 && (
                <p className="text-xs text-gray-500 italic mb-2">
                  Synced with: {syncMatrix[key].join(', ')}
                </p>
              )}

              <span
                className={`inline-block text-xs font-semibold px-2 py-1 rounded mb-2 ${
                  payment === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {payment === 'paid' ? 'Paid' : 'Unpaid'}
              </span>

              <button
                onClick={() => navigate(`/my-account/account-settings/software/${mod.route}`)}
                disabled={!hasAccess}
                className={`mt-2 px-4 py-2 rounded transition ${
                  hasAccess
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {hasAccess ? `Launch ${mod.name}` : 'Access Denied'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
